import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Play,
  Pause,
  Trash2,
  Settings,
  TrendingUp,
  TrendingDown,
  Clock,
  Brain,
  Zap,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { DCAStrategy, FREQUENCY_LABELS } from '../../types/dca';

export function DCAStrategyList() {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Mock strategies - will be replaced with real data
  const strategies: DCAStrategy[] = [
    {
      id: '1',
      name: 'Acumulacion ETH',
      userId: 'user1',
      status: 'active',
      sourceToken: {
        address: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
        symbol: 'USDC',
        name: 'USD Coin',
        decimals: 6,
        price: 1,
      },
      targetToken: {
        address: '0x0000000000000000000000000000000000000000',
        symbol: 'ETH',
        name: 'Ethereum',
        decimals: 18,
        price: 2245.50,
      },
      investmentAmount: '100',
      totalBudget: '5000',
      usedBudget: '1500',
      frequency: 'weekly',
      nextExecutionTime: Date.now() + 2 * 24 * 60 * 60 * 1000,
      startDate: Date.now() - 30 * 24 * 60 * 60 * 1000,
      smartTiming: true,
      dipBuying: true,
      dipPercentage: 5,
      executionCount: 15,
      totalTokensAcquired: '0.6789',
      averagePurchasePrice: '2209.34',
      currentValue: '1524.18',
      profitLoss: '+24.18',
      profitLossPercentage: 1.61,
      createdAt: Date.now() - 30 * 24 * 60 * 60 * 1000,
      updatedAt: Date.now(),
    },
    {
      id: '2',
      name: 'BTC Mensual',
      userId: 'user1',
      status: 'active',
      sourceToken: {
        address: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
        symbol: 'USDC',
        name: 'USD Coin',
        decimals: 6,
        price: 1,
      },
      targetToken: {
        address: '0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599',
        symbol: 'WBTC',
        name: 'Wrapped Bitcoin',
        decimals: 8,
        price: 43250.00,
      },
      investmentAmount: '500',
      totalBudget: '12000',
      usedBudget: '1000',
      frequency: 'monthly',
      nextExecutionTime: Date.now() + 15 * 24 * 60 * 60 * 1000,
      startDate: Date.now() - 60 * 24 * 60 * 60 * 1000,
      smartTiming: true,
      dipBuying: false,
      executionCount: 2,
      totalTokensAcquired: '0.02345',
      averagePurchasePrice: '42643.28',
      currentValue: '1014.21',
      profitLoss: '+14.21',
      profitLossPercentage: 1.42,
      createdAt: Date.now() - 60 * 24 * 60 * 60 * 1000,
      updatedAt: Date.now(),
    },
    {
      id: '3',
      name: 'NDH Acumulacion',
      userId: 'user1',
      status: 'paused',
      sourceToken: {
        address: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
        symbol: 'USDC',
        name: 'USD Coin',
        decimals: 6,
        price: 1,
      },
      targetToken: {
        address: '0x1234567890123456789012345678901234567890',
        symbol: 'NDH',
        name: 'Nardium Token',
        decimals: 18,
        price: 0.85,
      },
      investmentAmount: '50',
      totalBudget: '2000',
      usedBudget: '350',
      frequency: 'daily',
      nextExecutionTime: Date.now() + 24 * 60 * 60 * 1000,
      startDate: Date.now() - 14 * 24 * 60 * 60 * 1000,
      smartTiming: true,
      dipBuying: true,
      dipPercentage: 10,
      executionCount: 7,
      totalTokensAcquired: '423.52',
      averagePurchasePrice: '0.826',
      currentValue: '359.99',
      profitLoss: '+9.99',
      profitLossPercentage: 2.86,
      createdAt: Date.now() - 14 * 24 * 60 * 60 * 1000,
      updatedAt: Date.now(),
    },
  ];

  const handleToggleStrategy = (id: string, currentStatus: string) => {
    console.log('Toggle strategy:', id, currentStatus);
    // Will implement actual toggle logic
  };

  const handleDeleteStrategy = (id: string) => {
    console.log('Delete strategy:', id);
    // Will implement actual delete logic
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500/20 text-green-500 border-green-500/30">Activo</Badge>;
      case 'paused':
        return <Badge className="bg-yellow-500/20 text-yellow-500 border-yellow-500/30">Pausado</Badge>;
      case 'completed':
        return <Badge className="bg-blue-500/20 text-blue-500 border-blue-500/30">Completado</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-500/20 text-red-500 border-red-500/30">Cancelado</Badge>;
      default:
        return null;
    }
  };

  const formatTimeUntil = (timestamp: number) => {
    const diff = timestamp - Date.now();
    const days = Math.floor(diff / (24 * 60 * 60 * 1000));
    const hours = Math.floor((diff % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));

    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h`;
    return 'Pronto';
  };

  if (strategies.length === 0) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <div className="w-16 h-16 bg-violet-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <TrendingUp className="h-8 w-8 text-violet-500" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
            Sin estrategias DCA
          </h3>
          <p className="text-slate-600 dark:text-slate-400 mb-4">
            Crea tu primera estrategia de Dollar Cost Averaging para comenzar a acumular crypto automaticamente.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {strategies.map((strategy) => {
        const isExpanded = expandedId === strategy.id;
        const isProfit = strategy.profitLossPercentage >= 0;
        const progress = (parseFloat(strategy.usedBudget) / parseFloat(strategy.totalBudget)) * 100;

        return (
          <Card key={strategy.id} className="overflow-hidden">
            <CardContent className="p-0">
              {/* Main Row */}
              <div
                className="p-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                onClick={() => setExpandedId(isExpanded ? null : strategy.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {/* Token Icons */}
                    <div className="relative">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-r from-violet-500 to-purple-500 flex items-center justify-center text-white font-bold">
                        {strategy.targetToken.symbol.charAt(0)}
                      </div>
                      <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-xs">
                        {strategy.sourceToken.symbol.charAt(0)}
                      </div>
                    </div>

                    {/* Strategy Info */}
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                          {strategy.name}
                        </h3>
                        {getStatusBadge(strategy.status)}
                        {strategy.smartTiming && (
                          <Brain className="h-4 w-4 text-violet-500" aria-label="Smart Timing activo" />
                        )}
                        {strategy.dipBuying && (
                          <Zap className="h-4 w-4 text-amber-500" aria-label="Dip Buying activo" />
                        )}
                      </div>
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        ${strategy.investmentAmount} {strategy.sourceToken.symbol} → {strategy.targetToken.symbol} · {FREQUENCY_LABELS[strategy.frequency]}
                      </p>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-6">
                    <div className="text-right hidden md:block">
                      <p className="text-sm text-slate-600 dark:text-slate-400">Proxima compra</p>
                      <p className="font-medium text-slate-900 dark:text-slate-100 flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {formatTimeUntil(strategy.nextExecutionTime)}
                      </p>
                    </div>

                    <div className="text-right hidden md:block">
                      <p className="text-sm text-slate-600 dark:text-slate-400">Acumulado</p>
                      <p className="font-medium text-slate-900 dark:text-slate-100">
                        {strategy.totalTokensAcquired} {strategy.targetToken.symbol}
                      </p>
                    </div>

                    <div className="text-right">
                      <p className="text-sm text-slate-600 dark:text-slate-400">P&L</p>
                      <p className={`font-medium flex items-center gap-1 ${isProfit ? 'text-green-500' : 'text-red-500'}`}>
                        {isProfit ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                        {strategy.profitLossPercentage}%
                      </p>
                    </div>

                    {isExpanded ? (
                      <ChevronUp className="h-5 w-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-slate-400" />
                    )}
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mt-3">
                  <div className="flex justify-between text-xs text-slate-600 dark:text-slate-400 mb-1">
                    <span>${strategy.usedBudget} invertido</span>
                    <span>${strategy.totalBudget} presupuesto</span>
                  </div>
                  <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-violet-500 to-purple-500 transition-all duration-500"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {isExpanded && (
                <div className="px-4 pb-4 pt-2 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-slate-600 dark:text-slate-400">Precio Promedio</p>
                      <p className="font-medium text-slate-900 dark:text-slate-100">
                        ${strategy.averagePurchasePrice}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-600 dark:text-slate-400">Valor Actual</p>
                      <p className="font-medium text-slate-900 dark:text-slate-100">
                        ${strategy.currentValue}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-600 dark:text-slate-400">Ejecuciones</p>
                      <p className="font-medium text-slate-900 dark:text-slate-100">
                        {strategy.executionCount}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-600 dark:text-slate-400">Dip Trigger</p>
                      <p className="font-medium text-slate-900 dark:text-slate-100">
                        {strategy.dipBuying ? `${strategy.dipPercentage}%` : 'Desactivado'}
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant={strategy.status === 'active' ? 'outline' : 'default'}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleStrategy(strategy.id, strategy.status);
                      }}
                    >
                      {strategy.status === 'active' ? (
                        <>
                          <Pause className="mr-1 h-4 w-4" />
                          Pausar
                        </>
                      ) : (
                        <>
                          <Play className="mr-1 h-4 w-4" />
                          Reanudar
                        </>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        console.log('Edit strategy:', strategy.id);
                      }}
                    >
                      <Settings className="mr-1 h-4 w-4" />
                      Configurar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-500 hover:text-red-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteStrategy(strategy.id);
                      }}
                    >
                      <Trash2 className="mr-1 h-4 w-4" />
                      Eliminar
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
